import { useEffect, useState } from 'react';

/**
 * useDebounce Hook
 *
 * This hook debounces a value by delaying its update until after a specified delay period.
 * @template T - The type of the value to debounce.
 * @param value The value to debounce.
 * @param delay The delay in milliseconds for debouncing (default is 500ms).
 * @returns {T} The debounced value.
 */
const useDebounce = <T>(value: T, delay = 500): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

export default useDebounce;
